// src/components/sales/sections/ClosedDealsSection.jsx
import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Lightbulb, HandIcon, TrendingUp, AlertCircle, RefreshCw, Eye, BarChart3, List } from 'lucide-react';
import { componentThemes } from '../../../theme/colors';

// Brand palette for team members
const TEAM_COLORS = ['#0093cf','#009391','#92278f','#f58025','#ed145b','#b0bc22'];

const ClosedDealsSection = ({ data, offerData=null, loading, error, isEmpty }) => {
  const [viewMode, setViewMode] = useState('both');
  const cardCls = `${componentThemes.card.background} ${componentThemes.card.rounded} ${componentThemes.card.shadow} border ${componentThemes.card.border} ${componentThemes.card.padding}`;

  if (loading) return (
    <div className={cardCls}>
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><HandIcon className="h-4 w-4" />Closed Deals</h3>
      <div className="flex items-center justify-center h-48 text-grey-400"><div className="text-center"><RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" /><p>Loading deals…</p></div></div>
    </div>
  );

  if (error) return (
    <div className={cardCls}>
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><HandIcon className="h-4 w-4" />Closed Deals</h3>
      <div className="flex items-center justify-center h-48 text-pink-600"><div className="text-center"><AlertCircle className="h-8 w-8 mx-auto mb-2" /><p>{error}</p></div></div>
    </div>
  );

  if (!data?.length || isEmpty) return (
    <div className={cardCls}>
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><HandIcon className="h-4 w-4" />Closed Deals</h3>
      <div className="text-center text-grey-400 py-8"><HandIcon className="h-12 w-12 mx-auto mb-3 text-grey-300" /><p>No closed deals data available</p></div>
    </div>
  );

  const totalDeals  = data.reduce((s,m) => s+(m.deals||0), 0);
  const totalValue  = data.reduce((s,m) => s+(m.deal_value||0), 0);
  const avgDealSize = totalDeals>0 ? totalValue/totalDeals : 0;

  const teamData = data.filter(m=>(m.deals||0)>0).map((m,i) => ({
    ...m, color: TEAM_COLORS[i%TEAM_COLORS.length],
    percentage: totalValue>0 ? Math.round(m.deal_value/totalValue*100) : 0,
  }));

  const pieData = teamData.map(m => ({ name:m.name, value:m.deals||0, color:m.color }));

  // Offer types
  const OFFER_COLORS = ['#009391','#0093cf','#b0bc22','#0093cf','#ed145b'];
  const MOCK_OFFER_TYPES = [
    {name:'WS25K',count:12,color:OFFER_COLORS[0],percentage:26.7},
    {name:'WS20K',count:8, color:OFFER_COLORS[1],percentage:17.8},
    {name:'MM7K', count:15,color:OFFER_COLORS[2],percentage:33.3},
    {name:'MM5K', count:6, color:OFFER_COLORS[3],percentage:13.3},
    {name:'MN3K', count:4, color:OFFER_COLORS[4],percentage:8.9},
  ];
  const offerTypes = offerData?.by_type
    ? Object.entries(offerData.by_type).map(([t,c],i) => ({ name:t, count:c, color:OFFER_COLORS[i%OFFER_COLORS.length], percentage: totalDeals>0?+(c/totalDeals*100).toFixed(1):0 }))
    : MOCK_OFFER_TYPES.map(o => ({ ...o, count: totalDeals>0?Math.round(o.percentage/100*totalDeals):o.count }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active||!payload?.length) return null;
    return (
      <div className="bg-white p-3 border border-grey-200 rounded-lg shadow-lg">
        <p className="text-xs font-semibold text-darkblue-500">{payload[0].payload.name}</p>
        <p className="text-xs text-brightblue-600">{payload[0].value} deals</p>
      </div>
    );
  };

  return (
    <div className={cardCls}>
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-sm font-semibold text-darkblue-500 flex items-center gap-2">
          <HandIcon className="h-4 w-4" />Closed Deals
          {teamData.length>1 && (
            <div className="relative group ml-1">
              <Lightbulb className="h-4 w-4 text-yellow-500 cursor-pointer" />
              <div className="absolute left-0 mt-2 w-72 bg-white border border-grey-200 rounded-lg shadow-lg p-3 text-xs text-darkblue-500 z-10 hidden group-hover:block space-y-1">
                {(() => {
                  const top = teamData.reduce((p,c)=>p.deals>c.deals?p:c);
                  const topOffer = offerTypes.reduce((p,c)=>p.count>c.count?p:c);
                  return (
                    <>
                      <div>🏆 Top closer: <span className="font-semibold">{top.name}</span> — {top.deals} deals</div>
                      <div>📊 Avg: {(totalDeals/teamData.length).toFixed(1)} deals/person</div>
                      <div>💰 Most popular: <span className="font-semibold">{topOffer.name}</span></div>
                      <div>💵 Total: ${(totalValue/1000).toFixed(1)}K</div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </h3>

        {/* View toggle */}
        <div className="flex bg-grey-100 rounded-lg p-1">
          {[{key:'cards',Icon:BarChart3},{key:'chart',Icon:List},{key:'both',Icon:Eye}].map(({key,Icon}) => (
            <button key={key} onClick={()=>setViewMode(key)}
              className={`px-2.5 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors ${viewMode===key?'bg-white text-darkblue-500 shadow-sm':'text-grey-500 hover:text-darkblue-500'}`}>
              <Icon className="h-3 w-3" />{key.charAt(0).toUpperCase()+key.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Cards view */}
      {(viewMode==='cards'||viewMode==='both') && (
        <div className="space-y-3 mb-5">
          <h4 className="text-xs font-semibold text-darkblue-500 mb-2">Team Performance</h4>
          {teamData.map((m,i) => (
            <div key={m.name}>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold text-darkblue-500">{m.name}</span>
                <span className="text-grey-500">{m.deals} deals • ${((m.deal_value||0)/1000).toFixed(0)}K</span>
              </div>
              <div className="w-full bg-grey-200 rounded-full h-6 overflow-hidden">
                <div className="h-full rounded-full flex items-center justify-center text-white text-xs font-semibold"
                  style={{ backgroundColor:m.color, width:`${Math.max(m.percentage,5)}%` }}>
                  {m.percentage}%
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chart view */}
      {(viewMode==='chart'||viewMode==='both') && (
        <div className="flex items-center gap-6 mb-5">
          <div className="relative w-44 h-44 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value">
                  {pieData.map((e,i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-xl font-bold text-darkblue-500">{totalDeals}</div>
              <div className="text-xs text-grey-400">Deals</div>
            </div>
          </div>
          <div className="space-y-2">
            {pieData.map((m,i) => (
              <div key={m.name} className="flex items-center gap-2 text-xs">
                <div className="w-3.5 h-3.5 rounded flex-shrink-0" style={{backgroundColor:m.color}} />
                <span className="text-darkblue-500">{m.name}</span>
                <span className="font-semibold text-darkblue-500">{m.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Offer types */}
      <div className="mt-2">
        <h4 className="text-xs font-semibold text-darkblue-500 mb-3">Deals by Offer Type</h4>
        <div className="grid grid-cols-5 gap-2 mb-4">
          {offerTypes.map(o => (
            <div key={o.name} className="text-center p-3 rounded-xl text-white text-xs" style={{backgroundColor:o.color}}>
              <div className="text-lg font-bold">{o.count}</div>
              <div className="opacity-90">{o.name}</div>
              <div className="opacity-70 text-xs">{o.percentage}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-grey-200 mt-2">
        <div className="text-center">
          <div className="text-xl font-bold text-darkblue-500">{totalDeals}</div>
          <div className="text-xs text-grey-400">Total Deals</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-teal-700">${(totalValue/1000).toFixed(0)}K</div>
          <div className="text-xs text-grey-400">Total Value</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-bold text-brightblue-700">${(avgDealSize/1000).toFixed(0)}K</div>
          <div className="text-xs text-grey-400">Avg Deal Size</div>
        </div>
      </div>
    </div>
  );
};

export default ClosedDealsSection;